import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as RunbookIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import type { Incident, IncidentOpsState } from '../../services/incidentsApi';
import type {
  WorkflowCorrectiveAction,
  WorkflowCorrectiveActionEvidenceSummary,
  WorkflowClaimReadinessResponse,
  WorkflowImpactSummary,
} from '../../services/reportsApi';
import {
  incidentCanonicalFollowupOwnerLabel,
  incidentCanonicalRuleOwnerLabel,
} from './reviewCanonical';
import { AuditScopeBadge } from './GroupedIncidentReview';
import {
  MEASUREMENT_SAFE_HELPER,
  groupedStepTitle,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import {
  FOLLOW_UP_OWNER_FIELD_HELPER,
  FOLLOW_UP_STATUS_FIELD_LABEL,
  FOLLOW_UP_STATUS_OPTIONS,
  FOLLOW_UP_STATUS_SAVE_ALERT,
  FOLLOW_UP_STATUS_SECTION_SUBTITLE,
  FOLLOW_UP_TEAM_FIELD_HELPER,
} from '../../domain/incidents/followUpStatusSemantics';
import {
  FOLLOWUP_SAVE_BACKEND_NOTE,
} from '../../domain/incidents/incidentOwnershipSemantics';
import { FOLLOWUP_OWNER_LABEL, FOLLOWUP_TEAM_LABEL } from '../../domain/incidents/incidentPatternVocabulary';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import {
  FOLLOW_UP_MEASUREMENT_OUTCOME_OPTIONS,
  buildImpactTrackingSummary,
  formatMeasurementOutcomeLabel,
  TRACK_IMPACT_UNAVAILABLE_MESSAGE,
} from '../../domain/incidents/trackImpactSemantics';
import {
  correctiveActionFollowThroughForType,
} from '../../domain/incidents/groupedCorrectiveActionPresentation';
import {
  groupedRiskSourceFieldLabel,
} from '../../domain/incidents/groupedCorrectiveActionWorkflow';
import { formatGroupedReviewDateOnlyDisplay } from '../../pages/incidents/useIncidentsTimeRange';
import { isCanonicalClaimControlResolved } from '../../domain/incidents/groupedSafetyCaseReportReadiness';
import InvestigateControlLinkCard from './InvestigateControlLinkCard';

type IncidentDrawerOpsTabProps = {
  groupedReviewMode?: boolean;
  selectedIncident: Incident;
  opsStateDraft: IncidentOpsState;
  setOpsStateDraft: (value: IncidentOpsState) => void;
  opsAssigneeDraft: string;
  setOpsAssigneeDraft: (value: string) => void;
  opsTeamDraft: string;
  setOpsTeamDraft: (value: string) => void;
  opsSlaTargetDraft: string;
  setOpsSlaTargetDraft: (value: string) => void;
  measurementBaselineDraft: string;
  setMeasurementBaselineDraft: (value: string) => void;
  measurementFollowUpDraft: string;
  setMeasurementFollowUpDraft: (value: string) => void;
  measurementMeasuredAtDraft: string;
  setMeasurementMeasuredAtDraft: (value: string) => void;
  measurementOutcomeStatusDraft: string;
  setMeasurementOutcomeStatusDraft: (value: string) => void;
  measurementNoteDraft: string;
  setMeasurementNoteDraft: (value: string) => void;
  measurementImpactWaivedDraft?: boolean;
  setMeasurementImpactWaivedDraft?: (value: boolean) => void;
  correctiveActionTypeDraft?: string;
  setCorrectiveActionTypeDraft?: (value: string) => void;
  correctiveActionPriorityDraft?: string;
  setCorrectiveActionPriorityDraft?: (value: string) => void;
  correctiveActionDueDateDraft?: string;
  setCorrectiveActionDueDateDraft?: (value: string) => void;
  correctiveActionVerificationMethodDraft?: string;
  setCorrectiveActionVerificationMethodDraft?: (value: string) => void;
  correctiveActionControlReviewDraft?: import('../../domain/incidents/correctiveActionControlReview').CorrectiveActionControlReviewDraft;
  setCorrectiveActionControlReviewDraft?: (
    value: import('../../domain/incidents/correctiveActionControlReview').CorrectiveActionControlReviewDraft,
  ) => void;
  canUseOpsAutomation: boolean;
  canManageIncidentWorkflow?: boolean;
  drawerBusy: string | null;
  handleSaveOps: () => void | Promise<void>;
  handleSaveMeasurement: () => void | Promise<void>;
  handleSaveCoverageReviewAttestation?: (input: {
    reviewed: boolean;
    evidenceReference?: string | null;
  }) => void | Promise<void>;
  handleSaveZoneControlsConfirmation?: (input: {
    controlsPresent: boolean;
    controlsFunctioning: boolean;
    evidenceReference?: string | null;
  }) => void | Promise<void>;
  handleSaveCorrectiveAction?: () => void | Promise<void>;
  runbookTemplates: Array<any>;
  incidentRunbookBusy: string | null;
  setIncidentRunbookBusy: (value: string | null) => void;
  executeRunbookServer: (template: any, payload: any) => Promise<any>;
  navigate: (to: string) => void;
  user: any;
  canRunIncidentRunbooks: boolean;
  renderMode?: 'drawer' | 'page';
  step6Ref?: React.Ref<HTMLDivElement>;
  step7Ref?: React.Ref<HTMLDivElement>;
  step6Navigation?: React.ReactNode;
  step7Navigation?: React.ReactNode;
  /** Grouped guided workflow: split plan vs measurement across panels. */
  visibleSections?: 'all' | 'assign_action' | 'assign_action_plan' | 'assign_action_followup' | 'track_impact';
  onCreateWorkOrder?: () => void;
  canCreateWorkOrder?: boolean;
  createWorkOrderBusy?: boolean;
  workflowFeedback?: { severity: 'success' | 'info' | 'warning'; message: string } | null;
  trackImpactReady?: boolean;
  /** Parent Assign Action step provides the heading (Epic 93 owner gate). */
  suppressGroupedSectionHeading?: boolean;
  zoneLabel?: string | null;
  groupedCorrectiveActionLocked?: boolean;
  groupedCorrectiveActionPlanSaved?: boolean;
  groupedCorrectivePlanDraftDirty?: boolean;
  workflowId?: string;
  workflowCorrectiveActions?: {
    active_actions?: WorkflowCorrectiveAction[];
    completed_actions?: WorkflowCorrectiveAction[];
    cancelled_actions?: WorkflowCorrectiveAction[];
    evidence_summary?: WorkflowCorrectiveActionEvidenceSummary | null;
  };
  workflowImpactSummary?: WorkflowImpactSummary | null;
  workflowClaimReadiness?: WorkflowClaimReadinessResponse | null;
  refreshWorkflowCorrectiveSummary?: () => void | Promise<void>;
  handleAddWorkflowEvidenceNote?: (actionId: string, description: string) => void | Promise<void>;
  handleUpdateWorkflowActionStatus?: (actionId: string, status: 'completed' | 'validated') => void | Promise<void>;
  safetyCaseId?: string | null;
  orgId?: string | null;
  siteId?: string | null;
};

const extractActionId = (value: string): string | null => {
  const text = String(value || '').trim();
  if (!text) return null;
  const match = text.match(/^Run\s+([a-z0-9_]+)$/i) || text.match(/^([a-z0-9_]+)$/);
  return match ? match[1].toLowerCase() : null;
};

function readInlineValidationMeasurement(incident: Incident): Record<string, any> {
  const measurement = (incident as any)?.envelope_metadata?.measurement;
  return measurement && typeof measurement === 'object' ? measurement : {};
}

const IncidentDrawerOpsTab: React.FC<IncidentDrawerOpsTabProps> = ({
  groupedReviewMode = false,
  selectedIncident,
  opsStateDraft,
  setOpsStateDraft,
  opsAssigneeDraft,
  setOpsAssigneeDraft,
  opsTeamDraft,
  setOpsTeamDraft,
  opsSlaTargetDraft,
  setOpsSlaTargetDraft,
  measurementBaselineDraft,
  setMeasurementBaselineDraft,
  measurementFollowUpDraft,
  setMeasurementFollowUpDraft,
  measurementMeasuredAtDraft,
  setMeasurementMeasuredAtDraft,
  measurementOutcomeStatusDraft,
  setMeasurementOutcomeStatusDraft,
  measurementNoteDraft,
  setMeasurementNoteDraft,
  measurementImpactWaivedDraft = false,
  setMeasurementImpactWaivedDraft,
  correctiveActionTypeDraft = '',
  correctiveActionDueDateDraft = '',
  canUseOpsAutomation,
  drawerBusy,
  handleSaveOps,
  handleSaveMeasurement,
  handleSaveCoverageReviewAttestation,
  handleSaveZoneControlsConfirmation,
  runbookTemplates,
  incidentRunbookBusy,
  setIncidentRunbookBusy,
  executeRunbookServer,
  navigate,
  user,
  canRunIncidentRunbooks,
  renderMode = 'drawer',
  step6Ref,
  step7Ref,
  step6Navigation,
  step7Navigation,
  visibleSections = 'all',
  onCreateWorkOrder,
  canCreateWorkOrder = false,
  createWorkOrderBusy = false,
  workflowFeedback = null,
  trackImpactReady = true,
  suppressGroupedSectionHeading = false,
  zoneLabel = null,
  workflowId = '',
  workflowCorrectiveActions,
  workflowImpactSummary,
  workflowClaimReadiness,
  refreshWorkflowCorrectiveSummary,
  safetyCaseId = null,
  orgId = null,
  siteId = null,
}) => {
  const [groupedAdvancedOpen, setGroupedAdvancedOpen] = React.useState(false);
  const [coverageReviewedDraft, setCoverageReviewedDraft] = React.useState(false);
  const [coverageEvidenceReferenceDraft, setCoverageEvidenceReferenceDraft] = React.useState('');
  const [zoneControlsPresentDraft, setZoneControlsPresentDraft] = React.useState(false);
  const [zoneControlsFunctioningDraft, setZoneControlsFunctioningDraft] = React.useState(false);
  const [zoneControlsEvidenceReferenceDraft, setZoneControlsEvidenceReferenceDraft] = React.useState('');
  const showAssignActionPlan =
    visibleSections === 'all'
    || visibleSections === 'assign_action'
    || visibleSections === 'assign_action_plan';
  const showAssignActionFollowup =
    visibleSections === 'all'
    || visibleSections === 'assign_action'
    || visibleSections === 'assign_action_followup';
  const showAssignAction = showAssignActionPlan || showAssignActionFollowup;
  const showTrackImpact = visibleSections === 'all' || visibleSections === 'track_impact';
  const trackImpactFieldsEnabled = !groupedReviewMode || trackImpactReady;
  const hideActionOwnerField = groupedReviewMode && visibleSections === 'assign_action';
  const taxonomy: any = (selectedIncident as any)?.taxonomy || null;
  const step6Meta = groupedStepTitle('step6', groupedReviewMode);
  const step7Meta = groupedStepTitle('step7', groupedReviewMode);
  const ruleOwner = incidentCanonicalRuleOwnerLabel(selectedIncident) || String(taxonomy?.owner || '').trim();
  const followupOwner = incidentCanonicalFollowupOwnerLabel(selectedIncident);
  const description = String(taxonomy?.description || '').trim();
  const recommendedActions = Array.isArray(taxonomy?.recommended_actions) ? taxonomy.recommended_actions : [];
  const investigationSteps = Array.isArray(taxonomy?.investigation_steps) ? taxonomy.investigation_steps : [];
  const deviceId = String((selectedIncident as any)?.device_id || '').trim();
  const measurementReadiness = String((selectedIncident as any)?.safety_case?.outcome_measurement?.readiness_state || '').trim();
  const hasWorkOrder = Boolean(
    (selectedIncident as any)?.safety_case?.corrective_action?.has_work_order
    || (selectedIncident as any)?.safety_case?.action_snapshot?.has_work_order
    || (selectedIncident as any)?.odoo_ticket_id,
  );
  const workflowPrimaryAction = workflowCorrectiveActions?.active_actions?.[0]
    || workflowCorrectiveActions?.completed_actions?.[0]
    || null;
  const impactMeasurement = workflowImpactSummary?.impact_measurement || null;
  const coverageControlState = workflowClaimReadiness?.controls_by_type?.observation_coverage_reliability;
  const canonicalCoveragePayload = {
    ...(coverageControlState?.control_payload || {}),
    ...(coverageControlState?.result_payload || {}),
  };
  const hasCanonicalCoverage = Boolean(
    workflowClaimReadiness
    && (
      (Array.isArray(workflowClaimReadiness.controls) && workflowClaimReadiness.controls.length > 0)
      || Number(workflowClaimReadiness.total_count || 0) > 0
    )
    && Object.keys(canonicalCoveragePayload).length > 0,
  );
  const denominatorTrust = hasCanonicalCoverage
    ? String(canonicalCoveragePayload.denominator_trust || '').trim().toLowerCase()
    : String(impactMeasurement?.denominator_trust || '').trim().toLowerCase();
  const baselineCoverage = hasCanonicalCoverage
    ? canonicalCoveragePayload.baseline_observation_coverage_percent
    : impactMeasurement?.baseline_observation_coverage_percent;
  const followupCoverage = hasCanonicalCoverage
    ? canonicalCoveragePayload.followup_observation_coverage_percent
    : impactMeasurement?.followup_observation_coverage_percent;
  const matchedWindowReady = hasCanonicalCoverage
    ? isCanonicalClaimControlResolved(
      workflowClaimReadiness?.controls,
      'matched_window_followup_validation',
    )
    : workflowImpactSummary?.measurement_status === 'sufficient_data'
      && Boolean(workflowImpactSummary?.baseline_window?.start)
      && Boolean(workflowImpactSummary?.post_action_window?.start);
  const zoneControlsScopeLabel = String(
    zoneLabel
    || (selectedIncident as any)?.zone_id
    || '',
  ).trim();
  const selectedIncidentVersion = Number((selectedIncident as any)?.version || 0);
  const inlineValidationMeasurement = readInlineValidationMeasurement(selectedIncident);
  const savedCoverageReview = (
    inlineValidationMeasurement.coverage_review
    && typeof inlineValidationMeasurement.coverage_review === 'object'
  ) ? inlineValidationMeasurement.coverage_review : {};
  const savedZoneControlsConfirmation = (
    inlineValidationMeasurement.zone_controls_confirmation
    && typeof inlineValidationMeasurement.zone_controls_confirmation === 'object'
  ) ? inlineValidationMeasurement.zone_controls_confirmation : {};
  React.useEffect(() => {
    setCoverageReviewedDraft(savedCoverageReview.reviewed === true);
    setCoverageEvidenceReferenceDraft(String(savedCoverageReview.evidence_reference || ''));
    setZoneControlsPresentDraft(savedZoneControlsConfirmation.controls_present === true);
    setZoneControlsFunctioningDraft(savedZoneControlsConfirmation.controls_functioning === true);
    setZoneControlsEvidenceReferenceDraft(String(savedZoneControlsConfirmation.evidence_reference || ''));
  }, [
    selectedIncident?.incident_id,
    selectedIncidentVersion,
    savedCoverageReview.reviewed,
    savedCoverageReview.evidence_reference,
    savedZoneControlsConfirmation.controls_present,
    savedZoneControlsConfirmation.controls_functioning,
    savedZoneControlsConfirmation.evidence_reference,
  ]);

  const correctiveFollowThrough = groupedReviewMode
    ? correctiveActionFollowThroughForType(correctiveActionTypeDraft)
    : null;
  const groupedNextReviewDisplay = groupedReviewMode
    ? formatGroupedReviewDateOnlyDisplay(
      correctiveActionDueDateDraft
      || String(workflowPrimaryAction?.due_date || ''),
    )
    : null;
  const riskSourceLabel = groupedRiskSourceFieldLabel(ruleOwner);
  const impactSummary = buildImpactTrackingSummary({
    baselineCount: measurementBaselineDraft === '' ? null : Number(measurementBaselineDraft),
    followUpCount: measurementFollowUpDraft === '' ? null : Number(measurementFollowUpDraft),
    outcomeStatus: measurementOutcomeStatusDraft,
    readinessState: measurementReadiness,
    actionEffectiveAt: measurementMeasuredAtDraft,
  });
  const groupedTrackingInProgress = groupedReviewMode && measurementReadiness.trim().toLowerCase() === 'tracking_in_progress';
  const representativeWorkflowClosed = ['CLOSED', 'SUPPRESSED'].includes(
    String(selectedIncident?.status || '').trim().toUpperCase(),
  );

  const runIncidentRunbook = async (actionId: string) => {
    const template = runbookTemplates.find((value: any) => value.id === actionId);
    if (!template) return;
    setIncidentRunbookBusy(actionId);
    try {
      await executeRunbookServer(template, {
        device_id: deviceId || undefined,
        actor: user?.username,
        params: deviceId ? { device_id: deviceId } : {},
      });
      const params = new URLSearchParams();
      if (deviceId) params.set('device', deviceId);
      if (selectedIncident?.incident_id) params.set('incident_id', selectedIncident.incident_id);
      if (selectedIncident?.camera_id) params.set('camera_id', String(selectedIncident.camera_id));
      if (selectedIncident?.zone_id) params.set('zone_id', String(selectedIncident.zone_id));
      params.set('triage', '1');
      params.set('runbook', actionId);
      navigate(`/diagnostics?${params.toString()}`);
    } catch {
      // Error surfaced via parent UI.
    } finally {
      setIncidentRunbookBusy(null);
    }
  };

  return (
    <Box>
      {showAssignAction ? (
      <>
      {showAssignActionPlan ? (
      <Box ref={step6Ref} sx={{ mb: 2 }}>
        {!groupedReviewMode ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4, fontWeight: 800, letterSpacing: 0.9, textTransform: 'uppercase' }}>
          Action phase
        </Typography>
        ) : null}
        {!suppressGroupedSectionHeading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: groupedReviewMode ? 0.6 : 1 }}>
          <Typography component="span" variant="subtitle2" sx={{ fontWeight: 800 }}>
            {groupedReviewMode ? step6Meta.title : 'Step 6: Plan the action'}
          </Typography>
          {groupedReviewMode && step6Meta.badge ? (
            <AuditScopeBadge label={step6Meta.badge} />
          ) : null}
        </Box>
        ) : null}
        {groupedReviewMode && step6Meta.subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
            {step6Meta.subtitle}
          </Typography>
        ) : null}
        {groupedReviewMode ? (
          <>
          <InvestigateControlLinkCard
            safetyCaseId={safetyCaseId}
            orgId={orgId}
            siteId={siteId}
            workflowId={workflowId}
            incidentId={String(selectedIncident?.incident_id || '').trim() || null}
            incident={selectedIncident as { safety_case_id?: string | null; finding_id?: string | null; safety_case?: { safety_case_id?: string | null; finding_id?: string | null } | null }}
            workflowCorrectiveActions={workflowCorrectiveActions}
            navigate={navigate}
          />
          {showAssignActionPlan && handleSaveZoneControlsConfirmation ? (
            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.25, borderRadius: 2 }} data-testid="grouped-zone-controls-attestation">
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Zone-controls field confirmation
              </Typography>
              {zoneControlsScopeLabel ? (
                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700 }}>
                  {`Zone: ${zoneControlsScopeLabel}`}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.55 }}>
                Record the live field confirmation here so the claim-readiness blocker clears from typed workflow state,
                not from a note alone.
              </Typography>
              <Stack spacing={0.15} sx={{ mt: 0.85 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={zoneControlsPresentDraft}
                      disabled={!canUseOpsAutomation || !!drawerBusy}
                      onChange={(event) => setZoneControlsPresentDraft(event.target.checked)}
                    />
                  )}
                  label="Controls are present in the field"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={zoneControlsFunctioningDraft}
                      disabled={!canUseOpsAutomation || !!drawerBusy}
                      onChange={(event) => setZoneControlsFunctioningDraft(event.target.checked)}
                    />
                  )}
                  label="Controls are functioning as intended"
                />
              </Stack>
              <TextField
                fullWidth
                size="small"
                label="Evidence reference"
                value={zoneControlsEvidenceReferenceDraft}
                disabled={!canUseOpsAutomation || !!drawerBusy}
                onChange={(event) => setZoneControlsEvidenceReferenceDraft(event.target.value)}
                placeholder="Walkdown photo set, clip, or worksheet reference"
                sx={{ mt: 0.9 }}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.9, flexWrap: 'wrap' }} useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  disabled={
                    !canUseOpsAutomation
                    || !!drawerBusy
                    || !zoneControlsPresentDraft
                    || !zoneControlsFunctioningDraft
                    || !String(zoneControlsEvidenceReferenceDraft || '').trim()
                  }
                  onClick={() => void handleSaveZoneControlsConfirmation({
                    controlsPresent: zoneControlsPresentDraft,
                    controlsFunctioning: zoneControlsFunctioningDraft,
                    evidenceReference: zoneControlsEvidenceReferenceDraft,
                  })}
                >
                  Save zone confirmation
                </Button>
                {savedZoneControlsConfirmation.controls_present === true
                && savedZoneControlsConfirmation.controls_functioning === true ? (
                  <Chip size="small" color="success" label="Confirmed" />
                  ) : null}
              </Stack>
            </Paper>
          ) : null}
          </>
        ) : null}
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
          {ruleOwner && (
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {riskSourceLabel}: {ruleOwner}
            </Typography>
          )}
          {followupOwner && (
            <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.25 }}>
              {displaySafetyManagerLabel('followup_owner')}: {followupOwner}
            </Typography>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setGroupedAdvancedOpen((open) => !open)}
            data-testid="grouped-corrective-advanced-toggle"
            sx={{ mt: 0.75, px: 0, fontWeight: 700, textTransform: 'none' }}
          >
            {groupedAdvancedOpen ? 'Hide advanced details' : 'Advanced details'}
          </Button>
          <Collapse in={groupedAdvancedOpen}>
            {description && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            )}
            {recommendedActions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 800 }}>
                  Recommended actions
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {recommendedActions.slice(0, 6).map((action: string, index: number) => {
                    const actionId = extractActionId(action);
                    const template = actionId ? runbookTemplates.find((value: any) => value.id === actionId) : null;
                    const busy = incidentRunbookBusy === (template?.id || '');
                    return template && deviceId && canRunIncidentRunbooks ? (
                      <Button
                        key={index}
                        size="small"
                        variant="outlined"
                        startIcon={busy ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                        disabled={busy}
                        onClick={() => void runIncidentRunbook(template.id)}
                      >
                        {template.title}
                      </Button>
                    ) : (
                      <Chip key={index} label={String(action)} size="small" variant="outlined" sx={{ m: 0 }} />
                    );
                  })}
                </Box>
              </Box>
            )}
            {deviceId && canRunIncidentRunbooks && runbookTemplates.some((value: any) => value.id === 'convergence_recover') && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={
                    incidentRunbookBusy === 'convergence_recover' ? (
                      <CircularProgress size={14} />
                    ) : (
                      <RunbookIcon />
                    )
                  }
                  disabled={Boolean(incidentRunbookBusy)}
                  onClick={() => void runIncidentRunbook('convergence_recover')}
                  data-testid="grouped-guided-triage-advanced"
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                >
                  Advanced: Run guided triage
                </Button>
              </Box>
            )}
            {workflowFeedback ? (
              <Alert severity={workflowFeedback.severity} sx={{ mt: 1, mb: 1, borderRadius: 2 }} data-testid="grouped-work-order-feedback">
                {workflowFeedback.message}
              </Alert>
            ) : null}
            {onCreateWorkOrder && !hasWorkOrder ? (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!canCreateWorkOrder || createWorkOrderBusy}
                  onClick={onCreateWorkOrder}
                  data-testid="grouped-create-work-order"
                  sx={{ fontWeight: 700 }}
                >
                  {createWorkOrderBusy ? 'Linking Odoo work order…' : 'Link Odoo work order'}
                </Button>
              </Box>
            ) : null}
            {correctiveFollowThrough?.channelNote ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
                {correctiveFollowThrough.channelNote}
              </Typography>
            ) : null}
            {investigationSteps.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 800 }}>
                  Investigation steps
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                  {investigationSteps.slice(0, 8).map((step: string, index: number) => (
                    <li key={index}>
                      <Typography variant="body2">{String(step)}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
            {!ruleOwner && !followupOwner && !description && recommendedActions.length === 0 && investigationSteps.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No additional taxonomy guidance was surfaced for this incident.
              </Typography>
            )}
          </Collapse>
        </Paper>
        {step6Navigation}
      </Box>
      ) : null}

      {!groupedReviewMode || visibleSections === 'all' || visibleSections === 'assign_action' ? (
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
        {renderMode === 'page'
          ? 'Keep ownership, next review time, and action progress visible before this incident leaves the review workspace.'
          : 'Keep ownership, next review time, and action progress visible before this incident leaves review.'}
      </Typography>
      ) : null}
      {showAssignActionFollowup ? (
      <Box ref={step7Ref}>
        {!groupedReviewMode ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4, fontWeight: 800, letterSpacing: 0.9, textTransform: 'uppercase' }}>
          Action phase
        </Typography>
        ) : null}
        {!suppressGroupedSectionHeading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: groupedReviewMode ? 0.6 : 1 }}>
          <Typography component="span" variant="subtitle2" sx={{ fontWeight: 800 }}>
            {groupedReviewMode ? step7Meta.title : 'Step 7: Assign action owner and next review time'}
          </Typography>
          {groupedReviewMode && step7Meta.badge ? (
            <AuditScopeBadge label={step7Meta.badge} />
          ) : null}
        </Box>
        ) : null}
        {groupedReviewMode ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
            {FOLLOW_UP_STATUS_SECTION_SUBTITLE}
          </Typography>
        ) : step7Meta.subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
            {step7Meta.subtitle}
          </Typography>
        ) : null}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>{groupedReviewMode ? FOLLOW_UP_STATUS_FIELD_LABEL : 'Follow-up status'}</InputLabel>
              <Select
                label={groupedReviewMode ? FOLLOW_UP_STATUS_FIELD_LABEL : 'Follow-up status'}
                value={opsStateDraft}
                disabled={!canUseOpsAutomation}
                onChange={(event) => setOpsStateDraft((event.target.value as IncidentOpsState) || 'new')}
              >
                {FOLLOW_UP_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!hideActionOwnerField ? (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={groupedReviewMode ? FOLLOWUP_OWNER_LABEL : 'Owner / assignee'}
              value={opsAssigneeDraft}
              disabled={!canUseOpsAutomation}
              onChange={(event) => setOpsAssigneeDraft(event.target.value)}
              placeholder={groupedReviewMode ? 'e.g. ops-lead@site.com or site-ops-lead' : 'operator@site'}
              helperText={groupedReviewMode ? FOLLOW_UP_OWNER_FIELD_HELPER : undefined}
            />
          </Grid>
          ) : null}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label={groupedReviewMode ? FOLLOWUP_TEAM_LABEL : 'Team'}
              value={opsTeamDraft}
              disabled={!canUseOpsAutomation}
              onChange={(event) => setOpsTeamDraft(event.target.value)}
              placeholder="safety"
              helperText={groupedReviewMode ? FOLLOW_UP_TEAM_FIELD_HELPER : undefined}
            />
          </Grid>
          {groupedReviewMode && visibleSections === 'assign_action_followup' && groupedNextReviewDisplay ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" data-testid="grouped-next-review-saved">
                Next review date: <strong>{groupedNextReviewDisplay}</strong>
              </Typography>
            </Grid>
          ) : null}
          {!groupedReviewMode || visibleSections !== 'assign_action_followup' ? (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Next review time"
              value={opsSlaTargetDraft}
              disabled={!canUseOpsAutomation}
              onChange={(event) => setOpsSlaTargetDraft(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          ) : null}
          <Grid item xs={12}>
            {groupedReviewMode ? (
              <Alert severity="info" sx={{ mb: 1.25 }}>
                {hideActionOwnerField
                  ? `${FOLLOW_UP_STATUS_SAVE_ALERT} Set follow-up status, optional team label, and next review time. ${FOLLOWUP_SAVE_BACKEND_NOTE}`
                  : `${FOLLOW_UP_STATUS_SAVE_ALERT} ${FOLLOWUP_SAVE_BACKEND_NOTE}`}
              </Alert>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedIncident?.incident_id || !!drawerBusy || !canUseOpsAutomation}
                onClick={() => void handleSaveOps()}
              >
                {groupedReviewMode ? 'Save follow-up status' : 'Save action plan'}
              </Button>
              <Typography variant="caption" color="textSecondary">
                Saves action owner, next review time, and current action state together. This keeps the action plan attached to the incident but does not close the incident workflow. Escalations: {Number((selectedIncident as any)?.ops_escalation_level || 0)}
                {(selectedIncident as any)?.ops_escalation_reasons?.length
                  ? ` (${String((selectedIncident as any)?.ops_escalation_reasons?.join(', ') || '')})`
                  : ''}
              </Typography>
            </Box>
          </Grid>
          </Grid>
        </Paper>
        {step7Navigation}
      </Box>
      ) : null}
      </>
      ) : null}
      {showTrackImpact ? (
      <Box sx={{ mt: showAssignAction ? 2 : 0 }} data-testid="grouped-track-impact-panel">
        {groupedReviewMode ? (
          <>
            <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
              Impact measurement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25, lineHeight: 1.55 }}>
              Review the current impact state for this recurring risk pattern. Operators can annotate or waive while the
              system-owned pattern measurement evolves.
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4, fontWeight: 800, letterSpacing: 0.9, textTransform: 'uppercase' }}>
            Impact measurement
          </Typography>
        )}
        {groupedReviewMode && !trackImpactReady ? (
          <Alert severity="info" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="track-impact-not-ready">
            {TRACK_IMPACT_UNAVAILABLE_MESSAGE}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: groupedReviewMode ? 0.6 : 1 }}>
          <Typography component="span" variant="subtitle2" sx={{ fontWeight: 800 }}>
            {groupedReviewMode ? 'Review impact state' : displaySafetyManagerLabel('followup_measurement')}
          </Typography>
          {groupedReviewMode ? <AuditScopeBadge label="Representative evidence" /> : null}
        </Box>
        {groupedReviewMode ? (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, mb: 1.25, borderRadius: 2.5, bgcolor: 'grey.50' }}
            data-testid="grouped-impact-summary"
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Pattern observations before</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {workflowImpactSummary?.measurement_status === 'sufficient_data'
                    ? workflowImpactSummary.baseline_exposure_count
                    : impactSummary.baselineCount != null ? impactSummary.baselineCount : 'Not recorded'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Pattern observations after</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {workflowImpactSummary?.measurement_status === 'sufficient_data'
                    ? workflowImpactSummary.post_action_exposure_count
                    : impactSummary.followUpCount != null ? impactSummary.followUpCount : 'Not recorded'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Reduction</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {workflowImpactSummary?.measurement_status === 'sufficient_data'
                    ? `${workflowImpactSummary.percent_reduction ?? 0}%`
                    : impactSummary.reductionPercent != null ? `${impactSummary.reductionPercent}%` : 'Not recorded'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Outcome</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {workflowImpactSummary?.status === 'waived'
                    ? 'Waived'
                    : workflowImpactSummary?.measurement_status === 'sufficient_data'
                    ? 'Measured'
                    : formatMeasurementOutcomeLabel(measurementOutcomeStatusDraft)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Effectiveness conclusion</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.55 }}>
                  {workflowImpactSummary?.status === 'waived'
                    ? (workflowImpactSummary.waiver_summary || 'Impact measurement was explicitly waived.')
                    : workflowImpactSummary?.measurement_status === 'sufficient_data'
                    ? `Exposure rate changed from ${workflowImpactSummary.baseline_exposure_rate ?? 0}/day to ${workflowImpactSummary.post_action_exposure_rate ?? 0}/day across the measured before/after windows.`
                    : impactSummary.conclusion}
                </Typography>
              </Grid>
              {workflowImpactSummary?.status === 'waived' ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {`Waived by ${String(workflowImpactSummary.waived_by || 'operator')} on ${formatGroupedReviewDateOnlyDisplay(String(workflowImpactSummary.waived_at || '')) || 'the recorded date'}${workflowImpactSummary.waiver_reason ? ` · Reason: ${workflowImpactSummary.waiver_reason}` : ''}`}
                  </Typography>
                </Grid>
              ) : null}
              {workflowImpactSummary?.measurement_status === 'sufficient_data' ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {`Baseline window: ${formatGroupedReviewDateOnlyDisplay(String(workflowImpactSummary.baseline_window?.start || ''))} to ${formatGroupedReviewDateOnlyDisplay(String(workflowImpactSummary.baseline_window?.end || ''))} · Post-action window: ${formatGroupedReviewDateOnlyDisplay(String(workflowImpactSummary.post_action_window?.start || ''))} to ${formatGroupedReviewDateOnlyDisplay(String(workflowImpactSummary.post_action_window?.end || ''))}`}
                  </Typography>
                </Grid>
              ) : null}
              {(impactMeasurement || hasCanonicalCoverage) ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`Coverage review: baseline ${baselineCoverage != null ? `${baselineCoverage}%` : 'not available'} · follow-up ${followupCoverage != null ? `${followupCoverage}%` : 'not available'} · denominator trust ${denominatorTrust || 'not available'}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    {matchedWindowReady
                      ? 'Matched before/after windows are available for validation.'
                      : 'Matched-window follow-up validation is still pending enough comparable data.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    Coverage review and zone-control confirmation are recorded as structured validation attestations,
                    while matched-window validation auto-clears once this live measurement is sufficient.
                  </Typography>
                </Grid>
              ) : null}
              {groupedReviewMode && handleSaveCoverageReviewAttestation ? (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} data-testid="grouped-coverage-review-attestation">
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Coverage reliability review
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.55 }}>
                      Review the monitored-time / coverage reliability for these comparison windows here. This clears the
                      blocker from live workflow state instead of relying on a note-only validation task.
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={(
                        <Checkbox
                          checked={coverageReviewedDraft}
                          disabled={!canUseOpsAutomation || !!drawerBusy}
                          onChange={(event) => setCoverageReviewedDraft(event.target.checked)}
                        />
                      )}
                      label="I reviewed coverage reliability for these comparison windows"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Evidence reference"
                      value={coverageEvidenceReferenceDraft}
                      disabled={!canUseOpsAutomation || !!drawerBusy}
                      onChange={(event) => setCoverageEvidenceReferenceDraft(event.target.value)}
                      placeholder="Worksheet, chart, or audit reference"
                    />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.9, flexWrap: 'wrap' }} useFlexGap>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!canUseOpsAutomation || !!drawerBusy || !coverageReviewedDraft}
                        onClick={() => void handleSaveCoverageReviewAttestation({
                          reviewed: coverageReviewedDraft,
                          evidenceReference: coverageEvidenceReferenceDraft,
                        })}
                      >
                        Save coverage review
                      </Button>
                      {savedCoverageReview.reviewed === true ? (
                        <Chip size="small" color="success" label="Reviewed" />
                      ) : null}
                    </Stack>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          </Paper>
        ) : null}
        {groupedTrackingInProgress ? (
          <Alert severity="info" sx={{ mb: 1.25, borderRadius: 2 }} data-testid="grouped-impact-tracking-progress">
            Action applied and tracking is in progress. The system is waiting for enough follow-up time to measure
            before/after results honestly.
            {representativeWorkflowClosed
              ? ' The representative evidence can be closed while pattern-level impact measurement continues.'
              : ''}
          </Alert>
        ) : null}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            opacity: trackImpactFieldsEnabled ? 1 : 0.72,
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {groupedReviewMode
                  ? 'Pattern-level impact measurement should be system-computed. Use this section for operator context or waiver only.'
                  : 'Capture the baseline and follow-up outcome window on this incident so the canonical measurement state can move from ready-for-follow-up to measured.'}
                {measurementReadiness ? ` Current state: ${measurementReadiness.replace(/_/g, ' ')}.` : ''}
                {!groupedReviewMode ? ` ${MEASUREMENT_SAFE_HELPER}` : ''}
              </Typography>
              {groupedReviewMode && refreshWorkflowCorrectiveSummary ? (
                <Button
                  size="small"
                  variant="text"
                  disabled={!!drawerBusy}
                  onClick={() => void refreshWorkflowCorrectiveSummary()}
                  sx={{ mt: 0.5, px: 0 }}
                >
                  Refresh measured impact
                </Button>
              ) : null}
              {groupedReviewMode ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
              Confirm when the operator context or waiver applies. This timestamp is not the canonical Control implementation time and does not start Since Control measurement.
                </Typography>
              ) : null}
              {groupedReviewMode && !trackImpactFieldsEnabled ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                  Fields are shown for reference. Save becomes available after an action owner is assigned and follow-up begins.
                </Typography>
              ) : null}
            </Grid>
            {groupedReviewMode ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Operator context time"
                  value={measurementMeasuredAtDraft}
                  disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                  onChange={(event) => setMeasurementMeasuredAtDraft(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ) : null}
            {!groupedReviewMode ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Baseline count"
                    value={measurementBaselineDraft}
                    disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                    onChange={(event) => setMeasurementBaselineDraft(event.target.value)}
                    placeholder="10"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Follow-up count"
                    value={measurementFollowUpDraft}
                    disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                    onChange={(event) => setMeasurementFollowUpDraft(event.target.value)}
                    placeholder="4"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="Measured at"
                    value={measurementMeasuredAtDraft}
                    disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                    onChange={(event) => setMeasurementMeasuredAtDraft(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}>
                    <InputLabel>Outcome status</InputLabel>
                    <Select
                      label="Outcome status"
                      value={measurementOutcomeStatusDraft}
                      onChange={(event) => setMeasurementOutcomeStatusDraft(String(event.target.value || ''))}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select outcome</em>
                      </MenuItem>
                      {FOLLOW_UP_MEASUREMENT_OUTCOME_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label={groupedReviewMode ? 'Impact review note' : 'Measurement note'}
                value={measurementNoteDraft}
                disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                onChange={(event) => setMeasurementNoteDraft(event.target.value)}
                placeholder="Follow-up window confirms the risk dropped after corrective action."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={measurementImpactWaivedDraft}
                    disabled={!canUseOpsAutomation || !trackImpactFieldsEnabled}
                    onChange={(event) => setMeasurementImpactWaivedDraft?.(event.target.checked)}
                    data-testid="grouped-impact-measurement-waived"
                  />
                )}
                label="Waive impact measurement for final Safety Report sign-off (reason required in the note)"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  data-testid="grouped-save-impact-measurement"
                  disabled={
                    !selectedIncident?.incident_id
                    || !!drawerBusy
                    || !canUseOpsAutomation
                    || !trackImpactFieldsEnabled
                  }
                  onClick={() => void handleSaveMeasurement()}
                  sx={{ fontWeight: 800 }}
                >
                  {groupedReviewMode ? 'Save impact review' : 'Save follow-up measurement'}
                </Button>
                <Typography variant="caption" color="textSecondary">
                  {groupedReviewMode
                    ? 'Saves the action-implemented anchor, operator note, or waiver without asking the operator to enter counts.'
                    : 'Persists measurement on the incident so analytics and report scope rollups can reflect measured closure.'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      ) : null}
    </Box>
  );
};

export default IncidentDrawerOpsTab;
